import React, { useEffect, useMemo, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import ptBrLocale from '@fullcalendar/core/locales/pt-br';
import { INITIAL_EVENTS } from '../hook/event-utils.js';
import { API_BASE_URL } from './config';
import './calendar.css'

function AgendaLayout() {
    const [events] = useState(() => INITIAL_EVENTS.map((e) => ({ ...e })));
    const [activeTab, setActiveTab] = useState('calendar');
    const [scheduleRows, setScheduleRows] = useState([]);
    const [scheduleLoading, setScheduleLoading] = useState(false);
    const [scheduleError, setScheduleError] = useState('');
    const [scheduleView, setScheduleView] = useState('calendar');

    useEffect(() => {
        const fetchSchedule = async () => {
            const token = localStorage.getItem('token');
            if (!token) return;

            setScheduleLoading(true);
            setScheduleError('');
            try {
                const res = await fetch(`${API_BASE_URL}/horarios/me`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                let rows = [];
                if (res.ok) {
                    const data = await res.json();
                    rows = Array.isArray(data) ? data : [];
                } else {
                    const fallback = await fetch(`${API_BASE_URL}/turmas`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    if (fallback.ok) {
                        const data = await fallback.json();
                        rows = Array.isArray(data) ? data : [];
                    } else {
                        const msg = `Erro ao buscar horários (${res.status})`;
                        setScheduleError(msg);
                        setScheduleRows([]);
                        return;
                    }
                }

                const mapped = rows.map((row) => ({
                    diasSemana: row?.diasSemana ?? row?.cp_tr_dias_semana ?? '',
                    horarioInicio: row?.horarioInicio ?? row?.cp_tr_horario_inicio ?? '',
                    horarioFim: row?.horarioFim ?? row?.cp_tr_horario_fim ?? '',
                    turmaId: row?.turmaId ?? row?.cp_tr_id ?? null,
                    turmaNome: row?.turmaNome ?? row?.cp_tr_nome ?? '',
                    professorId: row?.professorId ?? row?.cp_tr_id_professor ?? null,
                    professorNome: row?.professorNome ?? row?.nomeDoProfessor ?? row?.professor_nome ?? '',
                    cursoId: row?.cursoId ?? row?.cp_tr_curso_id ?? null,
                    cursoNome: row?.cursoNome ?? row?.cp_nome_curso ?? '',
                    escolaId: row?.escolaId ?? row?.cp_tr_id_escola ?? null
                }));
                const schoolId = Number(localStorage.getItem('schoolId'));
                const filtered = Number.isFinite(schoolId) && schoolId > 0
                    ? mapped.filter((r) => Number(r?.escolaId) === schoolId)
                    : mapped;

                setScheduleRows(filtered);
            } catch (e) {
                setScheduleError('Erro ao buscar horários');
                setScheduleRows([]);
            } finally {
                setScheduleLoading(false);
            }
        };

        if (activeTab === 'horarios') {
            fetchSchedule();
        }
    }, [activeTab]);

    const normalizeText = (value) => String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();

    const parseDiasSemana = (diasSemana) => {
        const raw = normalizeText(diasSemana);
        if (!raw) return [];
        const tokens = raw.split(/[,;|/]+/).map((s) => s.trim()).filter(Boolean);
        const map = new Map([
            ['seg', 1], ['segunda', 1], ['segunda-feira', 1], ['monday', 1], ['mon', 1],
            ['ter', 2], ['terca', 2], ['terca-feira', 2], ['tuesday', 2], ['tue', 2],
            ['qua', 3], ['quarta', 3], ['quarta-feira', 3], ['wednesday', 3], ['wed', 3],
            ['qui', 4], ['quinta', 4], ['quinta-feira', 4], ['thursday', 4], ['thu', 4],
            ['sex', 5], ['sexta', 5], ['sexta-feira', 5], ['friday', 5], ['fri', 5],
            ['sab', 6], ['sabado', 6], ['sabado-feira', 6], ['saturday', 6], ['sat', 6],
            ['dom', 0], ['domingo', 0], ['sunday', 0], ['sun', 0],
        ]);

        const indexes = [];
        for (const t of tokens) {
            const idx = map.get(t);
            if (idx === 0 || Number.isFinite(idx)) indexes.push(idx);
        }
        return Array.from(new Set(indexes));
    };

    const parseTime = (value) => {
        const raw = String(value || '').trim();
        const m = raw.match(/^(\d{1,2}):(\d{2})$/);
        if (!m) return null;
        const hh = Math.min(23, Math.max(0, Number(m[1])));
        const mm = Math.min(59, Math.max(0, Number(m[2])));
        return { hh, mm };
    };

    const startOfWeekMonday = (baseDate) => {
        const d = new Date(baseDate);
        const day = d.getDay();
        const diff = (day + 6) % 7;
        d.setDate(d.getDate() - diff);
        d.setHours(0, 0, 0, 0);
        return d;
    };

    const addDays = (date, days) => {
        const d = new Date(date);
        d.setDate(d.getDate() + days);
        return d;
    };

    const toISODate = (date) => date.toISOString().slice(0, 10);

    const buildScheduleEvents = (rows) => {
        const monday = startOfWeekMonday(new Date());
        const result = [];

        for (const row of rows || []) {
            const dias = parseDiasSemana(row?.diasSemana);
            const startTime = parseTime(row?.horarioInicio);
            const endTime = parseTime(row?.horarioFim);
            if (!startTime) continue;

            const end = endTime || { hh: startTime.hh + 1, mm: startTime.mm };
            const dayIndexes = dias.length > 0 ? dias : [new Date().getDay()];

            for (const dayIdx of dayIndexes) {
                const offset = dayIdx === 0 ? 6 : dayIdx - 1;
                const date = addDays(monday, offset);
                const dateStr = toISODate(date);
                const start = `${dateStr}T${String(startTime.hh).padStart(2, '0')}:${String(startTime.mm).padStart(2, '0')}:00`;
                const endStr = `${dateStr}T${String(end.hh).padStart(2, '0')}:${String(end.mm).padStart(2, '0')}:00`;

                const titleParts = [];
                if (row?.turmaNome) titleParts.push(row.turmaNome);
                if (row?.professorNome) titleParts.push(row.professorNome);
                const title = titleParts.length > 0 ? titleParts.join(' - ') : 'Aula';

                result.push({
                    id: `sched-${row?.turmaId || 'matricula'}-${dayIdx}-${row?.horarioInicio || ''}-${row?.horarioFim || ''}`,
                    title,
                    start,
                    end: endStr,
                    extendedProps: {
                        turmaId: row?.turmaId || null,
                        professorId: row?.professorId || null,
                        professorNome: row?.professorNome || null,
                        cursoNome: row?.cursoNome || null,
                        escolaId: row?.escolaId || null
                    }
                });
            }
        }

        return result;
    };

    const scheduleEvents = useMemo(() => buildScheduleEvents(scheduleRows), [scheduleRows]);

    const scheduleEventsWithConflicts = useMemo(() => {
        const byDay = new Map();
        for (const ev of scheduleEvents) {
            const dayKey = String(ev.start).slice(0, 10);
            const list = byDay.get(dayKey) || [];
            list.push(ev);
            byDay.set(dayKey, list);
        }

        const withFlags = [];
        for (const [dayKey, list] of byDay.entries()) {
            const sorted = [...list].sort((a, b) => String(a.start).localeCompare(String(b.start)));
            const conflicts = new Set();

            const intervals = sorted.map((ev) => ({
                id: ev.id,
                startMs: new Date(ev.start).getTime(),
                endMs: new Date(ev.end || ev.start).getTime() || new Date(ev.start).getTime()
            }));

            for (let i = 0; i < intervals.length; i++) {
                for (let j = i + 1; j < intervals.length; j++) {
                    const a = intervals[i];
                    const b = intervals[j];
                    const overlap = a.startMs < b.endMs && b.startMs < a.endMs;
                    if (overlap) {
                        conflicts.add(a.id);
                        conflicts.add(b.id);
                    }
                }
            }

            for (const ev of sorted) {
                withFlags.push({ ...ev, extendedProps: { ...(ev.extendedProps || {}), conflict: conflicts.has(ev.id), dayKey } });
            }
        }

        return withFlags;
    }, [scheduleEvents]);

    return (
        <div className="agenda-container container-fluid p-3">
            <div className="card p-3">
                <ul className="nav nav-pills mb-3">
                    <li className="nav-item">
                        <button
                            className={`nav-link ${activeTab === 'calendar' ? 'active' : ''}`}
                            onClick={() => setActiveTab('calendar')}
                        >
                            Calendário
                        </button>
                    </li>
                    <li className="nav-item">
                        <button
                            className={`nav-link ${activeTab === 'horarios' ? 'active' : ''}`}
                            onClick={() => setActiveTab('horarios')}
                        >
                            Horários
                        </button>
                    </li>
                </ul>

                {activeTab === 'calendar' && (
                    <div className="row">
                        <div className="col-12">
                            <div className="card h-100 p-3">
                                <span className="card-title">Calendário</span>

                                <div className="calendar-wrapper mt-3">
                                    <FullCalendar
                                        plugins={[dayGridPlugin, interactionPlugin]}
                                        initialView="dayGridMonth"
                                        events={events}
                                        locale={ptBrLocale}
                                        headerToolbar={{
                                            left: 'prev,next today',
                                            center: 'title',
                                            right: 'dayGridMonth,dayGridWeek,dayGridDay',
                                        }}
                                        buttonText={{
                                            today: 'Hoje',
                                            month: 'Mês',
                                            week: 'Semana',
                                            day: 'Dia',
                                        }}
                                        eventTimeFormat={{
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            hour12: false,
                                        }}
                                        editable={false}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'horarios' && (
                    <div className="row">
                        <div className="col-12">
                            <div className="card h-100 p-3">
                                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                                    <span className="card-title">Horários de Aula</span>
                                    <div className="btn-group">
                                        <button
                                            type="button"
                                            className={`btn btn-sm ${scheduleView === 'calendar' ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={() => setScheduleView('calendar')}
                                        >
                                            Calendário
                                        </button>
                                        <button
                                            type="button"
                                            className={`btn btn-sm ${scheduleView === 'list' ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={() => setScheduleView('list')}
                                        >
                                            Lista
                                        </button>
                                    </div>
                                </div>

                                {scheduleLoading && <p className="text-secondary mt-3 mb-0">Carregando horários...</p>}
                                {!scheduleLoading && scheduleError && <p className="text-danger mt-3 mb-0">{scheduleError}</p>}
                                {!scheduleLoading && !scheduleError && scheduleRows.length === 0 && (
                                    <p className="text-secondary mt-3 mb-0">Nenhum horário cadastrado para o seu perfil.</p>
                                )}

                                {!scheduleLoading && !scheduleError && scheduleRows.length > 0 && scheduleView === 'calendar' && (
                                    <div className="calendar-wrapper mt-3">
                                        <FullCalendar
                                            plugins={[timeGridPlugin, interactionPlugin]}
                                            initialView="timeGridWeek"
                                            events={scheduleEventsWithConflicts}
                                            locale={ptBrLocale}
                                            headerToolbar={{
                                                left: 'prev,next today',
                                                center: 'title',
                                                right: 'timeGridWeek,timeGridDay',
                                            }}
                                            buttonText={{
                                                today: 'Hoje',
                                                week: 'Semana',
                                                day: 'Dia',
                                            }}
                                            slotMinTime="06:00:00"
                                            slotMaxTime="23:00:00"
                                            allDaySlot={false}
                                            slotLabelFormat={{
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                hour12: false,
                                            }}
                                            eventTimeFormat={{
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                hour12: false,
                                            }}
                                            eventContent={(arg) => {
                                                const conflict = Boolean(arg.event.extendedProps?.conflict);
                                                return (
                                                    <div className="d-flex flex-column">
                                                        <div className="d-flex align-items-center justify-content-between gap-2">
                                                            <span>{arg.timeText}</span>
                                                            {conflict && <span className="badge text-bg-danger">Conflito</span>}
                                                        </div>
                                                        <span>{arg.event.title}</span>
                                                    </div>
                                                );
                                            }}
                                        />
                                    </div>
                                )}

                                {!scheduleLoading && !scheduleError && scheduleRows.length > 0 && scheduleView === 'list' && scheduleEventsWithConflicts.length > 0 && (
                                    <div className="table-responsive mt-3">
                                        <table className="table table-sm">
                                            <thead>
                                                <tr>
                                                    <th>Dia</th>
                                                    <th>Início</th>
                                                    <th>Fim</th>
                                                    <th>Turma</th>
                                                    <th>Professor</th>
                                                    <th>Curso</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {scheduleEventsWithConflicts
                                                    .slice()
                                                    .sort((a, b) => String(a.start).localeCompare(String(b.start)))
                                                    .map((ev) => (
                                                        <tr key={ev.id}>
                                                            <td>{new Date(ev.start).toLocaleDateString('pt-BR')}</td>
                                                            <td>{String(ev.start).slice(11, 16)}</td>
                                                            <td>{String(ev.end || '').slice(11, 16)}</td>
                                                            <td>{String(ev.title || '').split(' - ')[0] || ''}</td>
                                                            <td>{ev.extendedProps?.professorNome || ''}</td>
                                                            <td>{ev.extendedProps?.cursoNome || ''}</td>
                                                            <td>{ev.extendedProps?.conflict ? <span className="badge text-bg-danger">Conflito</span> : <span className="badge text-bg-success">Sem conflito</span>}</td>
                                                        </tr>
                                                    ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {!scheduleLoading && !scheduleError && scheduleRows.length > 0 && scheduleView === 'list' && scheduleEventsWithConflicts.length === 0 && (
                                    <div className="table-responsive mt-3">
                                        <table className="table table-sm">
                                            <thead>
                                                <tr>
                                                    <th>Dias</th>
                                                    <th>Início</th>
                                                    <th>Fim</th>
                                                    <th>Turma</th>
                                                    <th>Professor</th>
                                                    <th>Curso</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {scheduleRows.map((r, idx) => (
                                                    <tr key={idx}>
                                                        <td>{r.diasSemana || '-'}</td>
                                                        <td>{r.horarioInicio || '-'}</td>
                                                        <td>{r.horarioFim || '-'}</td>
                                                        <td>{r.turmaNome || '-'}</td>
                                                        <td>{r.professorNome || '-'}</td>
                                                        <td>{r.cursoNome || '-'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default AgendaLayout;
